import { useCallback, useEffect, useState } from "react";
import {
    Loader2, Sparkles, Filter, Search, BadgeCheck, MapPin, Star, Phone,
    MessageCircle, Mail, UserCheck, ChevronDown, ChevronRight, X, Award,
    BookOpen, GraduationCap, Globe2, Calendar, Clock, Wallet, Target,
    Layers, TrendingUp, AlertTriangle, CheckCircle2, XCircle, Languages, Hash, MonitorSmartphone,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatPhoneDisplay, telLink, whatsappLink } from "../../lib/phone";
import TeamMemberAvatar from "../TeamMemberAvatar";

// V24 — Per-criterion chip metadata. Order ⇒ chip strip order.
const CRITERION_META = {
    grade: { label: "Grade", icon: GraduationCap },
    subject: { label: "Subject", icon: BookOpen },
    mode: { label: "Mode", icon: MonitorSmartphone },
    board: { label: "Board", icon: Layers },
    state: { label: "State", icon: MapPin },
    city: { label: "City", icon: MapPin },
    pin: { label: "PIN", icon: Hash },
    language: { label: "Lang", icon: Languages },
    preferred_days: { label: "Days", icon: Calendar },
    preferred_time: { label: "Time", icon: Clock },
    class_duration: { label: "Duration", icon: Clock },
    fee: { label: "Fee", icon: Wallet },
    exam_goal: { label: "Exam", icon: Target },
    curriculum: { label: "Curriculum", icon: Layers },
    learning_level: { label: "Level", icon: TrendingUp },
    gender_pref: { label: "Gender", icon: UserCheck },
    verified: { label: "Verified", icon: BadgeCheck },
    rating: { label: "Rating", icon: Star },
    experience: { label: "Exp", icon: Award },
    recency: { label: "Activity", icon: Sparkles },
};

// Which criteria to show ALWAYS visible as compact chips on the card header
// (the rest stay inside the "Why" expanded breakdown).
const KEY_CRITERIA_HOME = ["grade", "subject", "state", "city", "pin", "language", "preferred_days", "fee"];
const KEY_CRITERIA_ONLINE = ["grade", "subject", "language", "preferred_days", "preferred_time", "fee", "exam_goal", "curriculum"];

function criterionTone(score, max) {
    if (max <= 0) return "neutral";
    const ratio = score / max;
    if (ratio >= 0.85) return "green";
    if (ratio >= 0.5) return "amber";
    if (ratio > 0) return "amber-dim";
    return "red";
}

const TONE_CLASS = {
    green: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    "amber-dim": "bg-amber-50/60 text-amber-700 border-amber-100",
    red: "bg-red-50 text-red-700 border-red-200",
    neutral: "bg-slate-50 text-slate-600 border-slate-200",
};
const TONE_ICON = {
    green: <CheckCircle2 className="w-3 h-3" />,
    amber: <AlertTriangle className="w-3 h-3" />,
    "amber-dim": <AlertTriangle className="w-3 h-3 opacity-60" />,
    red: <XCircle className="w-3 h-3" />,
    neutral: null,
};

/**
 * RecommendedTutorsPanel — embedded inside the Lead Detail modal for any
 * DEMO (registrations) lead. Pulls the deterministic match list, supports
 * advanced filters + a manual tutor search override, and lets the admin
 * assign / contact a tutor with one click.
 *
 * The match score, label and per-criterion breakdown all come from the
 * backend (`/api/admin/leads/{id}/recommended-tutors`). The "✨ AI re-rank"
 * button calls Claude Haiku to reorder the visible top 10 with a one-line
 * rationale on each card.
 */
export default function RecommendedTutorsPanel({ leadId, lead, onAssigned }) {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [aiBusy, setAiBusy] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [assignBusy, setAssignBusy] = useState("");
    const [filters, setFilters] = useState({
        gender: "",
        min_experience: "",
        min_rating: "",
        mode: "",
        qualification: "",
        verified_only: false,
    });
    const [expanded, setExpanded] = useState({});
    // V16 — Multi-student view mode (only relevant when additional_students exist)
    const [excluded, setExcluded] = useState(0);
    const [excludedReasons, setExcludedReasons] = useState({});
    const hasSiblings = Array.isArray(lead?.additional_students) && lead.additional_students.length > 0;
    const [viewMode, setViewMode] = useState(hasSiblings ? "combined" : "single");
    const [multi, setMulti] = useState(null);
    const [activeStudent, setActiveStudent] = useState(0);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const params = new URLSearchParams();
            params.set("limit", "10");
            if (filters.gender) params.set("gender", filters.gender);
            if (filters.min_experience) params.set("min_experience", filters.min_experience);
            if (filters.min_rating) params.set("min_rating", filters.min_rating);
            if (filters.mode) params.set("mode", filters.mode);
            if (filters.qualification) params.set("qualification", filters.qualification);
            if (filters.verified_only) params.set("verified_only", "true");
            // Use multi endpoint when siblings exist (covers all three view modes)
            if (hasSiblings) {
                const r = await api.get(`/admin/leads/${leadId}/recommended-tutors/multi?${params.toString()}`);
                setMulti(r.data);
                const list = viewMode === "combined" ? (r.data.combined || []) : ((r.data.per_student || {})[String(activeStudent)] || []);
                setItems(list);
                setTotal(list.length);
            } else {
                const r = await api.get(`/admin/leads/${leadId}/recommended-tutors?${params.toString()}`);
                setItems(r.data.tutors || []);
                setTotal(r.data.total_candidates || 0);
                setExcluded(r.data.excluded_count || 0);
                setExcludedReasons(r.data.excluded_reasons || {});
            }
        } catch (e) {
            setError(e.response?.data?.detail || "Failed to load recommendations");
        } finally {
            setLoading(false);
        }
    }, [leadId, filters, hasSiblings, viewMode, activeStudent]);

    useEffect(() => { load(); }, [load]);

    const runAiRerank = async () => {
        if (!items.length) return;
        setAiBusy(true);
        try {
            const r = await api.post(`/admin/leads/${leadId}/recommended-tutors/ai-rerank`, { tutors: items });
            if (r.data.ai_used) {
                setItems(r.data.reranked);
                toast.success("AI re-ranked the shortlist");
            } else {
                toast.warning(r.data.error ? `AI fallback — ${r.data.error}` : "AI unavailable — showing deterministic order");
            }
        } catch (e) {
            toast.error("AI re-rank failed");
        } finally {
            setAiBusy(false);
        }
    };

    const assignTutor = async (tutor) => {
        setAssignBusy(tutor.id);
        try {
            await api.post(`/admin/leads/${leadId}/assign-tutor`, { tutor_id: tutor.id });
            toast.success(`Assigned ${tutor.name} to this demo`);
            onAssigned?.(tutor);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Assign failed");
        } finally {
            setAssignBusy("");
        }
    };

    const visible = items;

    return (
        <div className="bg-white rounded-xl border border-slate-200" data-testid="recommended-tutors-panel">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-orange-500" />
                    <h4 className="font-heading font-semibold text-[#0F172A]">Recommended Tutors</h4>
                    <span className="text-xs text-slate-400" data-testid="rec-tutors-summary">
                        {loading ? "scanning…" : (
                            <>
                                {`${visible.length} of ${total} candidates`}
                                {excluded > 0 && (
                                    <span
                                        className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800 font-semibold"
                                        title={Object.entries(excludedReasons).map(([k, v]) => `${k}: ${v}`).join("\n")}
                                        data-testid="rec-excluded-badge"
                                    >
                                        {excluded} excluded
                                    </span>
                                )}
                            </>
                        )}
                    </span>
                </div>
                <div className="flex items-center gap-1.5">
                    <button
                        onClick={runAiRerank}
                        disabled={aiBusy || !items.length}
                        data-testid="rec-tutors-ai-rerank"
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                    >
                        {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                        AI re-rank
                    </button>
                    <button
                        onClick={() => setFiltersOpen((v) => !v)}
                        data-testid="rec-tutors-filters-toggle"
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 bg-white border border-slate-200 text-slate-700 rounded-lg hover:border-slate-300"
                    >
                        <Filter className="w-3.5 h-3.5" /> Filters
                    </button>
                    <button
                        onClick={() => setSearchOpen((v) => !v)}
                        data-testid="rec-tutors-search-toggle"
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 bg-white border border-slate-200 text-slate-700 rounded-lg hover:border-slate-300"
                    >
                        <Search className="w-3.5 h-3.5" /> Manual search
                    </button>
                </div>
            </div>

            {filtersOpen && (
                <FiltersBar filters={filters} setFilters={setFilters} />
            )}

            {searchOpen && (
                <ManualSearchBar
                    onClose={() => setSearchOpen(false)}
                    leadId={leadId}
                    onAssigned={onAssigned}
                />
            )}

            {hasSiblings && multi && (
                <MultiStudentToggle
                    multi={multi}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    activeStudent={activeStudent}
                    setActiveStudent={setActiveStudent}
                />
            )}

            <div className="px-5 py-3 space-y-2">
                {loading && (
                    <div className="text-center py-10 text-slate-500 text-sm">
                        <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Scoring tutors…
                    </div>
                )}
                {!loading && error && (
                    <div className="text-center py-6 text-red-600 text-sm">{error}</div>
                )}
                {!loading && !error && visible.length === 0 && (
                    <div className="text-center py-10 text-slate-500 text-sm">
                        No matching tutors. Try widening the filters or use the manual search.
                    </div>
                )}
                {!loading && visible.map((t) => (
                    <TutorCard
                        key={`${t.id}-${t.student_index ?? "c"}`}
                        tutor={t}
                        viewMode={hasSiblings ? viewMode : "single"}
                        expanded={!!expanded[t.id]}
                        onToggle={() => setExpanded((m) => ({ ...m, [t.id]: !m[t.id] }))}
                        onAssign={() => assignTutor(t)}
                        assigning={assignBusy === t.id}
                    />
                ))}
            </div>
        </div>
    );
}

function FiltersBar({ filters, setFilters }) {
    const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
    return (
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs" data-testid="rec-tutors-filters">
            <Sel label="Gender" value={filters.gender} onChange={(v) => setF("gender", v)} options={["", "male", "female", "other"]} testid="filter-gender" />
            <Sel label="Mode" value={filters.mode} onChange={(v) => setF("mode", v)} options={["", "online", "home tuition", "both"]} testid="filter-mode" />
            <Sel label="Min experience" value={filters.min_experience} onChange={(v) => setF("min_experience", v)} options={["", "1", "2", "5", "10"]} testid="filter-experience" />
            <Sel label="Min rating" value={filters.min_rating} onChange={(v) => setF("min_rating", v)} options={["", "3", "4", "4.5"]} testid="filter-rating" />
            <label className="block">
                <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Qualification contains</span>
                <input value={filters.qualification} onChange={(e) => setF("qualification", e.target.value)} className="w-full px-2 py-1.5 rounded border border-slate-200" data-testid="filter-qualification" />
            </label>
            <label className="flex items-center gap-2 cursor-pointer pt-4">
                <input type="checkbox" checked={filters.verified_only} onChange={(e) => setF("verified_only", e.target.checked)} data-testid="filter-verified" />
                <span className="text-slate-700 font-semibold">Verified only</span>
            </label>
        </div>
    );
}

function Sel({ label, value, onChange, options, testid }) {
    return (
        <label className="block">
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</span>
            <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full px-2 py-1.5 rounded border border-slate-200 bg-white">
                {options.map((o) => <option key={o} value={o}>{o || "Any"}</option>)}
            </select>
        </label>
    );
}

function ManualSearchBar({ onClose, leadId, onAssigned }) {
    const [value, setValue] = useState("");
    const [results, setResults] = useState([]);
    const [assigning, setAssigning] = useState("");
    const runSearch = async () => {
        const q = value.trim();
        if (!q) { setResults([]); return; }
        try {
            const r = await api.get(`/admin/tutors/search?q=${encodeURIComponent(q)}&limit=20`);
            setResults(r.data.items || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Search failed");
        }
    };
    const assign = async (t) => {
        setAssigning(t.id);
        try {
            await api.post(`/admin/leads/${leadId}/assign-tutor`, { tutor_id: t.id });
            toast.success(`Assigned ${t.name}`);
            onAssigned?.(t);
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Assign failed");
        } finally {
            setAssigning("");
        }
    };
    return (
        <div className="px-5 py-3 bg-blue-50 border-b border-slate-100" data-testid="rec-tutors-search">
            <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-blue-700 shrink-0" />
                <input
                    autoFocus
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && runSearch()}
                    placeholder="Search by name, subject, city, email, phone or tutor ID"
                    className="flex-1 px-2 py-1.5 rounded border border-slate-200 text-sm"
                    data-testid="rec-search-input"
                />
                <button onClick={runSearch} data-testid="rec-search-go" className="px-3 py-1.5 bg-[#1E3A8A] text-white text-xs font-semibold rounded">Search</button>
                <button onClick={onClose} className="p-1.5 hover:bg-blue-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            {results.length > 0 && (
                <div className="mt-3 max-h-72 overflow-y-auto space-y-1" data-testid="rec-search-results">
                    {results.map((t) => (
                        <div key={t.id} className="flex items-center justify-between bg-white border border-blue-200 rounded-lg px-3 py-2">
                            <div className="min-w-0">
                                <div className="text-sm font-semibold text-[#0F172A]">{t.name}</div>
                                <div className="text-xs text-slate-500 truncate">{t.subjects || "—"} · {t.mode || "?"} · {t.city || "?"}</div>
                            </div>
                            <button
                                onClick={() => assign(t)}
                                disabled={assigning === t.id}
                                className="text-xs px-2.5 py-1 bg-orange-500 text-white rounded font-semibold disabled:opacity-60"
                                data-testid={`rec-search-assign-${t.id}`}
                            >
                                {assigning === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Assign"}
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function TutorCard({ tutor, viewMode = "single", expanded, onToggle, onAssign, assigning }) {
    const statusBadge = {
        available: "bg-emerald-100 text-emerald-700",
        busy: "bg-amber-100 text-amber-800",
        inactive: "bg-slate-100 text-slate-500",
    }[tutor.availability_status || "available"];

    // V24 — Build chip strip from breakdown (always-visible compact chips)
    const breakdownByCriterion = (tutor.match_breakdown || []).reduce((acc, b) => {
        acc[b.criterion] = b;
        return acc;
    }, {});
    const isOnline = (tutor.mode || "").toLowerCase().includes("online");
    const chipKeys = (isOnline ? KEY_CRITERIA_ONLINE : KEY_CRITERIA_HOME)
        .filter((k) => breakdownByCriterion[k]);

    // V24 — Completeness + AI Confidence
    const completeness = tutor.completeness || {};
    const tutorPct = completeness.tutor_completeness ?? null;
    const combinedPct = completeness.combined_completeness ?? null;
    const confidence = completeness.confidence ?? null;
    const isIncomplete = tutorPct !== null && tutorPct < 50;

    const confTone = {
        High: "bg-emerald-50 text-emerald-800 border-emerald-200",
        Medium: "bg-amber-50 text-amber-800 border-amber-200",
        Low: "bg-red-50 text-red-700 border-red-200",
    }[confidence] || "bg-slate-50 text-slate-600 border-slate-200";

    return (
        <div className="border border-slate-200 rounded-xl p-3 hover:border-orange-300 transition" data-testid={`tutor-card-${tutor.id}`}>
            <div className="flex items-start gap-3">
                <div className="shrink-0">
                    <TeamMemberAvatar
                        assetKey={tutor.photo_asset_key}
                        size="w-12 h-12"
                        alt={tutor.name}
                        ringClass="ring-2 ring-white shadow"
                        fallback={<span className="text-base font-bold text-orange-700">{(tutor.name || "?").charAt(0).toUpperCase()}</span>}
                    />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-2 mb-1">
                        <div className="font-semibold text-[#0F172A] truncate">{tutor.name}</div>
                        {tutor.verified && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] text-blue-700 font-semibold" title="Verified">
                                <BadgeCheck className="w-3 h-3" /> Verified
                            </span>
                        )}
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${statusBadge}`}>
                            {tutor.availability_status || "available"}
                        </span>
                        {typeof tutor.rating === "number" && tutor.rating > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 font-semibold" title="Rating">
                                <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> {tutor.rating.toFixed(1)}
                            </span>
                        )}
                    </div>
                    <div className="text-xs text-slate-600 truncate">{tutor.subjects || "—"} · {tutor.mode || "?"}{tutor.experience ? ` · ${tutor.experience}` : ""}{tutor.qualification ? ` · ${tutor.qualification}` : ""}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                        {tutor.city && <><MapPin className="w-3 h-3" /> {tutor.city}{tutor.areas?.length ? ` · ${tutor.areas.slice(0, 3).join(", ")}` : ""}</>}
                    </div>

                    {/* V24 — 3-stat header: Match | Completeness | Confidence */}
                    <div className="mt-2 flex items-stretch gap-1.5 flex-wrap" data-testid={`tutor-stats-${tutor.id}`}>
                        <Stat
                            label="MATCH"
                            value={`${Math.round(tutor.match_score)}%`}
                            sub={tutor.match_label}
                            tone={tutor.match_score >= 80 ? "green" : tutor.match_score >= 50 ? "amber" : "red"}
                            testid={`tutor-score-${tutor.id}`}
                            extra={{ "data-score": tutor.match_score }}
                            primary
                        />
                        {combinedPct !== null && (
                            <Stat
                                label="COMPLETENESS"
                                value={`${combinedPct}%`}
                                sub={tutorPct !== null ? `Tutor ${tutorPct}%` : null}
                                tone={combinedPct >= 75 ? "green" : combinedPct >= 50 ? "amber" : "red"}
                                testid={`tutor-completeness-${tutor.id}`}
                                extra={{ "data-completeness": combinedPct }}
                            />
                        )}
                        {confidence && (
                            <Stat
                                label="AI CONFIDENCE"
                                value={confidence}
                                tone={confidence === "High" ? "green" : confidence === "Medium" ? "amber" : "red"}
                                toneClass={confTone}
                                testid={`tutor-confidence-${tutor.id}`}
                                extra={{ "data-confidence": confidence }}
                            />
                        )}
                    </div>

                    {/* V24 — Always-visible per-criterion chip strip */}
                    {chipKeys.length > 0 && (
                        <div className="mt-2 -mx-1 px-1 flex items-center gap-1.5 overflow-x-auto pb-1" data-testid={`tutor-chip-strip-${tutor.id}`}>
                            {chipKeys.map((k) => {
                                const b = breakdownByCriterion[k];
                                const meta = CRITERION_META[k] || { label: k, icon: Award };
                                const tone = criterionTone(b.score, b.max);
                                const Icon = meta.icon;
                                return (
                                    <span
                                        key={k}
                                        title={`${meta.label}: ${b.reason}`}
                                        data-testid={`tutor-chip-${tutor.id}-${k}`}
                                        data-tone={tone}
                                        className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${TONE_CLASS[tone]}`}
                                    >
                                        {TONE_ICON[tone]}
                                        <Icon className="w-3 h-3" />
                                        {meta.label}
                                    </span>
                                );
                            })}
                        </div>
                    )}

                    {/* V24 — "Complete your profile" CTA when tutor data is sparse */}
                    {isIncomplete && (
                        <div
                            className="mt-2 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 flex items-start gap-1.5"
                            data-testid={`tutor-incomplete-cta-${tutor.id}`}
                        >
                            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-700" />
                            <span>
                                <span className="font-semibold">Incomplete tutor profile ({tutorPct}%).</span>{" "}
                                Ask this tutor to add languages, preferred days/time, fee &amp; exam categories to improve future AI recommendations.
                            </span>
                        </div>
                    )}

                    {viewMode === "combined" && typeof tutor.coverage === "number" && (
                        <div className="mt-1.5 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1" data-testid={`tutor-combined-${tutor.id}`}>
                            <Award className="inline w-3 h-3 mr-1" />
                            Combined match: covers {tutor.coverage}/{tutor.total_students} students well
                            {tutor.per_student_scores && (
                                <span className="ml-1">
                                    ({tutor.per_student_scores.map((p) => `${p.student_name}: ${Math.round(p.score)}`).join(" · ")})
                                </span>
                            )}
                        </div>
                    )}
                    {tutor.ai_rationale && (
                        <div className="mt-1.5 text-[11px] text-violet-700 bg-violet-50 border border-violet-100 rounded px-2 py-1" data-testid={`tutor-ai-rationale-${tutor.id}`}>
                            <Sparkles className="inline w-3 h-3 mr-1" />{tutor.ai_rationale}
                        </div>
                    )}
                </div>
            </div>
            <div className="mt-2 flex items-center flex-wrap gap-1.5">
                {tutor.phone && (
                    <a href={telLink(tutor.phone)} className="inline-flex items-center gap-1 text-xs px-3 py-2 sm:py-1 bg-blue-50 text-[#1E3A8A] rounded font-semibold hover:bg-blue-100 active:bg-blue-200" data-testid={`tutor-call-${tutor.id}`}>
                        <Phone className="w-3.5 h-3.5" /> <span className="hidden xs:inline sm:inline">{formatPhoneDisplay(tutor.phone)}</span><span className="xs:hidden sm:hidden">Call</span>
                    </a>
                )}
                {tutor.phone && (
                    <a href={whatsappLink(tutor.phone)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-3 py-2 sm:py-1 bg-green-50 text-[#25D366] rounded font-semibold hover:bg-green-100 active:bg-green-200" data-testid={`tutor-wa-${tutor.id}`}>
                        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                    </a>
                )}
                {tutor.email && (
                    <a href={`mailto:${tutor.email}`} className="inline-flex items-center gap-1 text-xs px-3 py-2 sm:py-1 bg-slate-100 text-slate-700 rounded font-semibold hover:bg-slate-200 active:bg-slate-300" data-testid={`tutor-email-${tutor.id}`}>
                        <Mail className="w-3.5 h-3.5" /> Email
                    </a>
                )}
                <button
                    onClick={onToggle}
                    data-testid={`tutor-toggle-${tutor.id}`}
                    className="inline-flex items-center gap-1 text-xs px-3 py-2 sm:py-1 bg-white border border-slate-200 text-slate-600 rounded font-semibold hover:border-slate-300"
                >
                    {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    {expanded ? "Hide" : "Why"}
                </button>
                <button
                    onClick={onAssign}
                    disabled={assigning}
                    data-testid={`tutor-assign-${tutor.id}`}
                    className="ml-auto inline-flex items-center justify-center gap-1 text-xs px-4 py-2 bg-orange-500 text-white rounded-lg font-bold uppercase tracking-wider hover:bg-orange-600 active:bg-orange-700 disabled:opacity-60 min-w-[6.5rem]"
                >
                    {assigning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5" />}
                    Assign
                </button>
            </div>
            {expanded && (
                <div className="mt-3 bg-slate-50 rounded-lg p-3 border border-slate-100 text-xs space-y-1.5" data-testid={`tutor-breakdown-${tutor.id}`}>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Full AI Score Breakdown — 20 criteria</div>
                    {(tutor.match_breakdown || []).map((b) => {
                        const meta = CRITERION_META[b.criterion] || { label: b.criterion, icon: Award };
                        const tone = criterionTone(b.score, b.max);
                        const Icon = meta.icon;
                        const barColor = {
                            green: "bg-emerald-500", amber: "bg-amber-500",
                            "amber-dim": "bg-amber-400", red: "bg-red-500", neutral: "bg-slate-400",
                        }[tone];
                        return (
                            <div key={b.criterion} className="flex items-center gap-2" data-testid={`tutor-breakdown-row-${tutor.id}-${b.criterion}`} data-tone={tone}>
                                <div className="w-28 flex items-center gap-1.5 text-slate-600">
                                    <Icon className="w-3 h-3 text-slate-400" />
                                    <span className="font-medium">{meta.label}</span>
                                </div>
                                <div className="flex-1 bg-slate-200 rounded-full h-1.5 overflow-hidden">
                                    <div className={`h-full ${barColor}`} style={{ width: `${Math.min(100, (b.score / Math.max(b.max, 0.01)) * 100)}%` }} />
                                </div>
                                <div className="w-14 text-right font-mono text-slate-700">{b.score}/{b.max}</div>
                                <div className="text-slate-500 hidden md:block flex-shrink-0 truncate w-[220px]" title={b.reason}>{b.reason}</div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// V24 — Reusable stat pill for Match | Completeness | Confidence header
function Stat({ label, value, sub, tone, toneClass, testid, extra = {}, primary = false }) {
    const cls = toneClass || TONE_CLASS[tone] || TONE_CLASS.neutral;
    return (
        <div
            data-testid={testid}
            {...extra}
            className={`flex flex-col items-center justify-center px-2 py-1 rounded-lg border min-w-[68px] ${cls} ${primary ? "ring-1 ring-current/20" : ""}`}
        >
            <span className="text-[9px] font-bold uppercase tracking-wider opacity-80 leading-tight">{label}</span>
            <span className={`font-bold ${primary ? "text-sm" : "text-xs"} leading-tight`}>{value}</span>
            {sub && <span className="text-[9px] opacity-70 leading-tight truncate max-w-[80px]">{sub}</span>}
        </div>
    );
}

function MultiStudentToggle({ multi, viewMode, setViewMode, activeStudent, setActiveStudent }) {
    const students = multi?.students || [];
    const combinedTop = (multi?.combined || [])[0];
    return (
        <div className="px-5 py-3 bg-amber-50/50 border-b border-amber-100" data-testid="multi-student-toggle">
            <div className="flex items-center flex-wrap gap-2 mb-2">
                <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-amber-900">Multi-Student Enquiry</span>
                <span className="text-[11px] text-slate-600">{students.length} students under this lead — view tutors as:</span>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
                <button
                    type="button"
                    onClick={() => setViewMode("combined")}
                    data-testid="rec-mode-combined"
                    data-on={viewMode === "combined"}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition ${
                        viewMode === "combined"
                            ? "bg-emerald-600 border-emerald-600 text-white"
                            : "bg-white border-slate-200 text-slate-700 hover:border-emerald-300"
                    }`}
                >
                    ✓ Combined Tutor
                    {combinedTop && viewMode !== "combined" && (
                        <span className="ml-1 text-[10px] opacity-70">(top: {Math.round(combinedTop.combined_score)}%)</span>
                    )}
                </button>
                <button
                    type="button"
                    onClick={() => setViewMode("per_student")}
                    data-testid="rec-mode-per-student"
                    data-on={viewMode === "per_student"}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition ${
                        viewMode === "per_student"
                            ? "bg-blue-600 border-blue-600 text-white"
                            : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"
                    }`}
                >
                    ✓ Separate Tutors
                </button>
                {viewMode === "per_student" && (
                    <div className="flex items-center gap-1 ml-2 flex-wrap">
                        {students.map((s, idx) => (
                            <button
                                key={idx}
                                type="button"
                                onClick={() => setActiveStudent(idx)}
                                data-testid={`rec-student-tab-${idx}`}
                                data-on={activeStudent === idx}
                                className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                                    activeStudent === idx
                                        ? "bg-blue-100 border-blue-400 text-blue-900 font-semibold"
                                        : "bg-white border-slate-200 text-slate-600 hover:border-blue-300"
                                }`}
                            >
                                {s.name || `Student ${idx + 1}`}
                                {s.grade && <span className="opacity-60 ml-1">· {s.grade}</span>}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

